import config from '../config/environment';

// Autocompletado de direcciones con Places API (New) de Google, por REST: no hace
// falta cargar el script de Maps. La key (REACT_APP_GOOGLE_MAPS_API_KEY) está
// restringida a los dominios de la app y a esta API, así que puede ir en el front.
//
// Cada búsqueda lleva un token de sesión: Google cobra las sugerencias mientras se
// escribe más el detalle de la elegida como una sola sesión.

const BASE = 'https://places.googleapis.com/v1';

export const placesDisponible = () => Boolean(config.googleMapsApiKey);

/** Error con el status de Google: 400 y 403 son la key rechazada, no un corte de red. */
function errorDeGoogle(r) {
    const error = new Error(`Google Places respondió ${r.status}`);
    error.status = r.status;
    return error;
}

export const keyRechazada = (error) => error.status === 400 || error.status === 403;

export function nuevaSesion() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Tipos de sugerencia en los que Google conoce la altura, no solo la calle.
const CON_ALTURA = ['street_address', 'premise', 'subpremise'];

/**
 * Sugerencias para lo que se lleva escrito: [{ id, principal, secundario, conAltura }].
 * conAltura es false si Google conoce la calle pero no ese número ("route").
 */
export async function sugerencias(texto, sesion, signal) {
    const r = await fetch(`${BASE}/places:autocomplete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': config.googleMapsApiKey },
        body: JSON.stringify({
            input: texto,
            sessionToken: sesion,
            includedRegionCodes: ['ar'],
            languageCode: 'es',
            // Solo direcciones: una calle sin altura (route) también, y el número se
            // completa abajo.
            includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route'],
        }),
        signal,
    });
    if (!r.ok) throw errorDeGoogle(r);
    const d = await r.json();
    return (d.suggestions || [])
        .map((s) => s.placePrediction)
        .filter(Boolean)
        .map((p) => ({
            id: p.placeId,
            principal: p.structuredFormat?.mainText?.text || p.text?.text || '',
            secundario: p.structuredFormat?.secondaryText?.text || '',
            conAltura: (p.types || []).some((t) => CON_ALTURA.includes(t)),
        }));
}

/** Componentes de la dirección elegida: { addressComponents, formattedAddress, location }. */
export async function detalle(placeId, sesion) {
    const params = new URLSearchParams({ languageCode: 'es', sessionToken: sesion });
    const r = await fetch(`${BASE}/places/${encodeURIComponent(placeId)}?${params}`, {
        headers: {
            'X-Goog-Api-Key': config.googleMapsApiKey,
            'X-Goog-FieldMask': 'addressComponents,formattedAddress,location',
        },
    });
    if (!r.ok) throw errorDeGoogle(r);
    return r.json();
}

/**
 * Lugares que Google encuentra para una dirección escrita entera ("Los Condores 699,
 * Villa Carlos Paz, Córdoba"), con los mismos campos que detalle() más el id:
 * sirve para confirmar que la calle y la altura existen en esa localidad.
 */
export async function buscarDireccion(texto, signal) {
    const r = await fetch(`${BASE}/places:searchText`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': config.googleMapsApiKey,
            'X-Goog-FieldMask': 'places.id,places.addressComponents,places.formattedAddress,places.location',
        },
        body: JSON.stringify({ textQuery: texto, regionCode: 'ar', languageCode: 'es', pageSize: 5 }),
        signal,
    });
    if (!r.ok) throw errorDeGoogle(r);
    const d = await r.json();
    return d.places || [];
}
